import { Box, IconButton, TableCell, TableRow, Tooltip, Typography } from "@mui/material";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import React from "react";

import { FROZEN_COLUMN_COUNT, FROZEN_RIGHT, ROW_BG, frozenCellSx } from "./frozenColumns";

interface ArchiveRowProps {
  /** Archived projects (every folder's). */
  count: number;
  /** Story maps of archived projects; shown while browsing story maps. */
  storyCount?: number;
  colSpan: number;
  highlighted: boolean;
  isDropTarget: boolean;
  onOpen: () => void;
  onHoverChange: (hovering: boolean) => void;
  /** Fly the map to where the archived projects are. */
  canLocate: boolean;
  onLocate: () => void;
  /** Dropping a project row here archives it. */
  dropProps: React.HTMLAttributes<HTMLElement>;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** The grey folder at the top level that archived projects live in. Opens like a folder; drop a project on it to archive it. */
export const ARCHIVE_COLOUR = "#64748b";

export default function ArchiveRow({
  count,
  storyCount,
  colSpan,
  highlighted,
  isDropTarget,
  onOpen,
  onHoverChange,
  canLocate,
  onLocate,
  dropProps,
}: ArchiveRowProps) {
  const summary = [
    storyCount !== undefined ? plural(storyCount, "story map") : null,
    plural(count, "project"),
  ]
    .filter(Boolean)
    .join(" · ");
  const base = isDropTarget ? ROW_BG.dropTarget : highlighted ? ROW_BG.hover : ROW_BG.base;
  return (
    <TableRow
      onClick={onOpen}
      onMouseEnter={() => onHoverChange(true)}
      onMouseLeave={() => onHoverChange(false)}
      {...dropProps}
      sx={{
        cursor: "pointer",
        borderLeft: highlighted ? `3px solid ${ARCHIVE_COLOUR}` : "3px solid transparent",
        backgroundColor: base,
        "&:hover": { backgroundColor: isDropTarget ? ROW_BG.dropTarget : ROW_BG.hover },
        transition: "background-color 120ms ease",
        ...(isDropTarget && { outline: "2px solid #00d4ad", outlineOffset: -2 }),
      }}
    >
      <TableCell colSpan={Math.max(1, colSpan - FROZEN_COLUMN_COUNT)} sx={{ py: 0.75 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Inventory2OutlinedIcon sx={{ color: ARCHIVE_COLOUR }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography noWrap sx={{ fontWeight: 600 }}>
              Archive
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {summary}
              {isDropTarget ? " · drop to archive" : ""}
            </Typography>
          </Box>
        </Box>
      </TableCell>
      {/* Same frozen columns as a folder row; nothing to pin or hide here. */}
      <TableCell padding="none" sx={frozenCellSx(FROZEN_RIGHT.pin)} />
      <TableCell padding="none" sx={frozenCellSx(FROZEN_RIGHT.eye)} />
      <TableCell padding="none" sx={frozenCellSx(FROZEN_RIGHT.locate)}>
        <Tooltip title={canLocate ? "Show on map" : "Nothing archived has a map area"} placement="left">
          <span>
            <IconButton
              size="small"
              disabled={!canLocate}
              aria-label="Show on map"
              onClick={(e) => {
                e.stopPropagation();
                onLocate();
              }}
              sx={{ color: "#64748b", "&:hover": { color: "#00d4ad" } }}
            >
              <MyLocationIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
      <TableCell padding="none" sx={frozenCellSx(FROZEN_RIGHT.menu, true)} />
    </TableRow>
  );
}
